import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { GraduationCap, Plus, ArrowsClockwise, MagnifyingGlass, CaretDown, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const CoursesConfig = () => {
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [courses, setCourses] = useState([]);
  const [loadingCampuses, setLoadingCampuses] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [formCampusId, setFormCampusId] = useState('');
  const [campusPickerOpen, setCampusPickerOpen] = useState(false);
  const [campusQuery, setCampusQuery] = useState('');
  const [form, setForm] = useState({ course_name: '', university_name: '', college_name: '', total_course_year: '4' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/campuses`, { withCredentials: true, headers: authHeaders() });
        const list = Array.isArray(data) ? data : [];
        setCampuses(list);
        if (list.length > 0) setCampusId(String(list[0].id));
      } catch (e) {
        toast.error(formatApiError(e, 'Failed to load campuses'));
      } finally {
        setLoadingCampuses(false);
      }
    })();
  }, []);

  const fetchCourses = useCallback(async (cid) => {
    if (!cid) { setCourses([]); return; }
    setLoadingCourses(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/student-courses?campus_id=${cid}`, { withCredentials: true, headers: authHeaders() });
      setCourses(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load courses'));
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  useEffect(() => { fetchCourses(campusId); }, [campusId, fetchCourses]);

  const selectedCampus = campuses.find(c => String(c.id) === String(campusId));
  const formCampus = campuses.find(c => String(c.id) === String(formCampusId));

  const filteredCampuses = useMemo(() => {
    const term = campusQuery.trim().toLowerCase();
    if (!term) return campuses;
    return campuses.filter(c =>
      String(c.campus_name || '').toLowerCase().includes(term) ||
      String(c.entity_name || '').toLowerCase().includes(term)
    );
  }, [campuses, campusQuery]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter(c =>
      (c.course_name || '').toLowerCase().includes(q) ||
      (c.university_name || '').toLowerCase().includes(q) ||
      (c.college_name || '').toLowerCase().includes(q) ||
      String(c.id).includes(q)
    );
  }, [courses, search]);

  const openAddDialog = () => {
    const cid = campusId ? String(campusId) : (campuses[0] ? String(campuses[0].id) : '');
    const camp = campuses.find(c => String(c.id) === cid);
    const name = camp?.campus_name || '';
    setFormCampusId(cid);
    setForm({
      course_name: '',
      university_name: name,
      college_name: name,
      total_course_year: '4',
    });
    setAddOpen(true);
  };

  // University name IS the campus (they're the same). Selecting a campus sets
  // both the university & college names to that campus name.
  const handleFormCampusChange = (cid) => {
    setFormCampusId(cid);
    const camp = campuses.find(c => String(c.id) === String(cid));
    const name = camp?.campus_name || '';
    setForm(prev => ({
      ...prev,
      university_name: name,
      college_name: name,
    }));
  };

  const handleAdd = async () => {
    if (!formCampusId) { toast.error('Pick a campus first'); return; }
    if (!form.course_name.trim()) { toast.error('Course name is required'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/student-courses`,
        {
          campus_id: String(formCampusId),
          course_name: form.course_name.trim(),
          university_name: form.university_name.trim(),
          college_name: form.college_name.trim(),
          total_course_year: String(form.total_course_year || '').trim(),
        },
        { withCredentials: true, headers: authHeaders() },
      );
      toast.success(`Course "${form.course_name.trim()}" added`);
      setAddOpen(false);
      // Show the campus we just added to (switching triggers a fetch via effect).
      if (String(formCampusId) !== String(campusId)) {
        setCampusId(String(formCampusId));
      } else {
        await fetchCourses(campusId);
      }
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add course'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-lg p-6" data-testid="courses-config">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-2">
          <GraduationCap size={20} className="text-[#0A2540]" />
          <h2 className="text-lg font-semibold text-[#0A2540] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Student Courses
          </h2>
        </div>
        <Button
          onClick={openAddDialog}
          disabled={loadingCampuses || campuses.length === 0}
          className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="add-course-btn"
        >
          <Plus size={16} className="mr-2" />
          Add Course
        </Button>
      </div>
      <p className="text-sm text-[#78716C] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Manage the courses available for each campus. New courses are posted directly to the middleware and become available in subscriber registration immediately.
      </p>

      {/* Campus picker + search + refresh */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-full sm:w-80 space-y-1.5">
          <Label className="text-xs text-[#78716C]">Campus</Label>
          <Select value={campusId} onValueChange={setCampusId} disabled={loadingCampuses}>
            <SelectTrigger className="border-[#EDE6D8] rounded-lg h-10" data-testid="courses-campus-select">
              <SelectValue placeholder={loadingCampuses ? 'Loading campuses…' : 'Select campus'} />
            </SelectTrigger>
            <SelectContent>
              {campuses.map(c => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.campus_name}{c.entity_name ? ` — ${c.entity_name}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px] space-y-1.5">
          <Label className="text-xs text-[#78716C]">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code or ID…"
              className="pl-9 border-[#EDE6D8] rounded-lg h-10"
              data-testid="courses-search-input"
            />
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchCourses(campusId)}
          disabled={!campusId || loadingCourses}
          className="border-[#EDE6D8] text-[#0A2540] rounded-lg h-10"
          data-testid="courses-refresh-btn"
        >
          <ArrowsClockwise size={15} className={`mr-2 ${loadingCourses ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Courses table */}
      <div className="border border-[#EDE6D8] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="courses-table">
          <thead>
            <tr className="bg-[#FCFAF6] text-left">
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-16" style={{ fontFamily: 'JetBrains Mono, monospace' }}>ID</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Course Name</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>University</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>College</th>
              <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#78716C] font-semibold w-20" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Years</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EDE6D8]">
            {loadingCourses && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#78716C]" data-testid="courses-loading">Loading courses…</td></tr>
            )}
            {!loadingCourses && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[#78716C]" data-testid="courses-empty">
                  {campusId ? (search ? 'No courses match your search.' : 'No courses configured for this campus yet.') : 'Select a campus to view its courses.'}
                </td>
              </tr>
            )}
            {!loadingCourses && filtered.map(c => (
              <tr key={c.id} className="hover:bg-[#FCFAF6]/60" data-testid={`course-row-${c.id}`}>
                <td className="px-4 py-2.5 text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.id}</td>
                <td className="px-4 py-2.5 font-medium text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.course_name}</td>
                <td className="px-4 py-2.5 text-xs text-[#78716C]">{c.university_name || '—'}</td>
                <td className="px-4 py-2.5 text-xs text-[#78716C]">{c.college_name || '—'}</td>
                <td className="px-4 py-2.5 text-xs text-[#78716C]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.total_course_year || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loadingCourses && courses.length > 0 && (
        <p className="text-xs text-[#78716C] mt-3" data-testid="courses-count">
          {filtered.length === courses.length
            ? `${courses.length} course${courses.length === 1 ? '' : 's'} on ${selectedCampus?.campus_name || 'this campus'}`
            : `${filtered.length} of ${courses.length} courses shown`}
        </p>
      )}

      {/* Add Course dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Add Course</DialogTitle>
            <DialogDescription>
              Posts to the middleware for <span className="font-semibold text-[#0A2540]">{formCampus?.campus_name || 'the selected campus'}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>University Name <span className="text-[#EF4444]">*</span></Label>
                <Popover open={campusPickerOpen} onOpenChange={(v) => { setCampusPickerOpen(v); if (!v) setCampusQuery(''); }}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="course-university-select"
                      className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-[#EDE6D8] bg-transparent px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20"
                    >
                      <span className={`flex-1 min-w-0 truncate ${formCampus ? 'text-[#1C1917]' : 'text-[#A8A29E]'}`}>
                        {formCampus
                          ? `${formCampus.campus_name}${formCampus.entity_name ? ` — ${formCampus.entity_name}` : ''}`
                          : 'Select university / campus'}
                      </span>
                      <CaretDown size={16} className="text-[#78716C] shrink-0" weight="bold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0 rounded-lg border-[#EDE6D8] shadow-lg"
                    align="start"
                    sideOffset={6}
                  >
                    <div className="flex items-center gap-2 border-b border-[#EDE6D8] px-3 py-2">
                      <MagnifyingGlass size={16} className="text-[#78716C] shrink-0" />
                      <Input
                        value={campusQuery}
                        onChange={(e) => setCampusQuery(e.target.value)}
                        placeholder="Search campus…"
                        autoFocus
                        data-testid="course-university-search"
                        className="h-8 border-0 px-0 py-0 shadow-none focus-visible:ring-0 text-sm placeholder:text-[#A8A29E]"
                      />
                    </div>
                    <div className="overflow-y-auto py-1 max-h-[280px]" data-testid="course-university-list">
                      {filteredCampuses.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-[#78716C] text-center">No campuses match.</div>
                      ) : (
                        filteredCampuses.map((c) => {
                          const isSel = String(c.id) === String(formCampusId);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => { handleFormCampusChange(String(c.id)); setCampusPickerOpen(false); setCampusQuery(''); }}
                              data-testid={`course-university-option-${c.id}`}
                              className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[#FCFAF6] transition-colors ${isSel ? 'bg-[#FCFAF6]' : ''}`}
                            >
                              <span className="flex-1 min-w-0 truncate text-[#1C1917]">
                                {c.campus_name}
                                {c.entity_name ? <span className="text-[#A8A29E] ml-1">— {c.entity_name}</span> : null}
                              </span>
                              {isSel && <Check size={16} className="text-[#0A2540] shrink-0" weight="bold" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label>Course Name <span className="text-[#EF4444]">*</span></Label>
                <Input
                  value={form.course_name}
                  onChange={(e) => setForm({ ...form, course_name: e.target.value })}
                  placeholder="e.g., Bachelor of Engineering"
                  className="border-[#EDE6D8] rounded-lg h-10"
                  data-testid="course-name-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>College Name</Label>
                <Input
                  value={form.college_name}
                  onChange={(e) => setForm({ ...form, college_name: e.target.value })}
                  placeholder="College"
                  className="border-[#EDE6D8] rounded-lg h-10"
                  data-testid="course-college-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Total Course Years</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={form.total_course_year}
                  onChange={(e) => setForm({ ...form, total_course_year: e.target.value })}
                  className="border-[#EDE6D8] rounded-lg h-10"
                  data-testid="course-years-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving} className="border-[#EDE6D8] rounded-lg" data-testid="course-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving || !formCampusId || !form.course_name.trim()} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="course-save-btn">
              {saving ? 'Saving…' : 'Add Course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
